import { Avatar, List, notification, Table, TableProps } from '@pankod/refine-antd';
import { useCallback, useEffect, useState } from 'react';
import { SearchNew } from 'components';
import { getInventories } from 'services/inventory';
import defaultAvatar from 'assets/images/default-avatar.jpg';
import { SearchIndex } from 'services/meilisearch';
import MetaData from 'utilities/metadata';
interface IProductState {
  id: string;
  sku: string;
  name: string;
  avatar: string | null;
  unitPrice: number;
  quantity: number;
  totalStockValue: number;
}

export const WarehouseProducts = () => {
  const [products, setProducts] = useState<IProductState[]>([]);
  const [totalProducts, setTotalProducts] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchText, setSearchText] = useState<string>('');

  const warehouseId = window.location.pathname.split('/')[2];

  const fetchDataOnMountCallback = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getInventories({
        warehouse: warehouseId,
        searchString: searchText,
        start: page,
        limit,
      });
      const productsData = data.data.map((item) => ({
        id: item.productVariant.id,
        sku: item.productVariant.sku,
        name: item.productVariant.name,
        avatar: item.productVariant.avatar || null,
        unitPrice: item.unitPrice,
        quantity: item.quantity,
        totalStockValue: item.totalStockValue,
      }));
      setProducts(productsData);
      setTotalProducts(data.total);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
    setIsLoading(false);
  }, [limit, page, searchText, warehouseId]);

  useEffect(() => {
    fetchDataOnMountCallback();
  }, [fetchDataOnMountCallback]);

  const onSelectOption = async (searchText: string) => {
    setSearchText(searchText);
    setPage(1);
  };

  const onPageChange: TableProps<IProductState>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra
  ) => {
    setPage(pagination.current ?? 1);
    setLimit(pagination.pageSize ?? 10);
  };

  return (
    <List title="All Products">
      <MetaData />
      <SearchNew
        placeholder="Search by SKU, Name, Tags, Category"
        size="large"
        className="search"
        index={SearchIndex.Product}
        groups={['sku', 'name', 'tag', 'category']}
        onSelectOption={onSelectOption}
        onReset={fetchDataOnMountCallback}
      />
      <Table
        dataSource={products}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: true }}
        onChange={onPageChange}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          total: totalProducts,
          current: page,
        }}
      >
        <Table.Column dataIndex="sku" title="SKU" />
        <Table.Column<IProductState>
          dataIndex="avatar"
          title="Image"
          width="6%"
          render={(_text, record): React.ReactNode => {
            return (
              <Avatar
                size={36}
                style={{ objectFit: 'cover' }}
                src={record.avatar || defaultAvatar}
                alt={`Avatar`}
              />
            );
          }}
        />
        <Table.Column dataIndex="name" title="Name" />
        <Table.Column dataIndex="unitPrice" title="Unit Price" />
        <Table.Column dataIndex="quantity" title="Units" />
        <Table.Column dataIndex="totalStockValue" title="Total Stock Value" />
      </Table>
    </List>
  );
};
